import React, { useState, useRef } from 'react';
import { Volume2, VolumeX, Loader2, Pause, Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bouton "Écouter" pour synthèse vocale des réponses IA
 * 
 * Usage:
 * <SpeakButton text={responseText} voice="nova" />
 */
export default function SpeakButton({ 
  text, 
  voice = "nova",
  className = "",
  size = "sm" // sm, md, lg
}) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  const sizeClasses = {
    sm: "p-1.5 text-xs",
    md: "p-2 text-sm",
    lg: "p-3 text-base"
  };

  const iconSizes = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6"
  };

  const handleSpeak = async () => {
    if (!text || text.trim().length === 0) return;

    // Si déjà en lecture, pause/resume
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }

    // Si audio déjà chargé, rejouer
    if (audioRef.current && audioRef.current.src) {
      audioRef.current.play();
      setPlaying(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tts/speak`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: text.substring(0, 4096), // Limite TTS
          voice: voice,
          speed: 1.0
        })
      });

      if (!response.ok) {
        throw new Error('Erreur génération audio');
      }

      const data = await response.json();
      
      if (data.success && data.audio_base64) {
        // Créer l'audio depuis le base64
        const audioSrc = `data:audio/mp3;base64,${data.audio_base64}`;
        
        if (audioRef.current) {
          audioRef.current.src = audioSrc;
          audioRef.current.play();
          setPlaying(true);
        }
      } else {
        throw new Error(data.error || 'Erreur audio');
      }
    } catch (err) {
      console.error('TTS Error:', err);
      setError('Erreur audio');
    } finally {
      setLoading(false);
    }
  };

  const handleAudioEnd = () => {
    setPlaying(false);
  };

  const handleAudioPause = () => {
    setPlaying(false);
  };

  const handleAudioPlay = () => {
    setPlaying(true);
  };

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <audio 
        ref={audioRef}
        onEnded={handleAudioEnd}
        onPause={handleAudioPause}
        onPlay={handleAudioPlay}
        className="hidden"
      />
      
      <button
        onClick={handleSpeak}
        disabled={loading || !text}
        className={`
          ${sizeClasses[size]}
          rounded-lg transition-all
          ${playing 
            ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }
          ${loading ? 'opacity-50 cursor-wait' : ''}
          disabled:opacity-30 disabled:cursor-not-allowed
          flex items-center gap-1
        `}
        title={playing ? "Mettre en pause" : "Écouter"}
        data-testid="speak-button"
      >
        {loading ? (
          <Loader2 className={`${iconSizes[size]} animate-spin`} />
        ) : playing ? (
          <Pause className={iconSizes[size]} />
        ) : (
          <Volume2 className={iconSizes[size]} />
        )}
        {size !== 'sm' && (
          <span>{playing ? 'Pause' : 'Écouter'}</span>
        )}
      </button>

      {error && (
        <span className="text-xs text-red-500">{error}</span>
      )}
    </div>
  );
}


/**
 * Version compacte pour les messages de chat
 */
export function SpeakButtonCompact({ text, voice = "nova" }) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const speak = async () => {
    if (!text) return;

    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }

    if (audioRef.current?.src) {
      audioRef.current.play();
      setPlaying(true);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tts/speak`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: text.substring(0, 4096), voice })
      });
      const data = await res.json();
      
      if (data.success && data.audio_base64) {
        audioRef.current.src = `data:audio/mp3;base64,${data.audio_base64}`;
        audioRef.current.play();
        setPlaying(true);
      }
    } catch (err) {
      console.error('TTS:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <audio 
        ref={audioRef} 
        onEnded={() => setPlaying(false)}
        className="hidden" 
      />
      <button
        onClick={speak}
        disabled={loading || !text}
        className={`p-1 rounded hover:bg-gray-100 transition-colors ${playing ? 'text-blue-600' : 'text-gray-400'}`}
        title="Écouter"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : playing ? (
          <VolumeX className="w-4 h-4" />
        ) : (
          <Volume2 className="w-4 h-4" />
        )}
      </button>
    </>
  );
}
